import BetDetail from "./views/BetDetail";
import Board from "./views/Board";
import Graph from "./views/Graph";
import InitiativeDetail from "./views/InitiativeDetail";
import ObjectDetail from "./views/ObjectDetail";
import ObjectNew from "./views/ObjectNew";
import Portfolio from "./views/Portfolio";
import Settings from "./views/Settings";
import Strategy from "./views/Strategy";
import TaskDetail from "./views/TaskDetail";
import Tasks from "./views/Tasks";
import { useSignOut, useUser } from "./auth";
import { useRoute, type Route } from "./route";
import { useStore } from "./store";

const TABS: { hash: string; label: string }[] = [
  { hash: "#/strategy", label: "Strategy" },
  { hash: "#/", label: "Services" },
  { hash: "#/assets", label: "Assets" },
  { hash: "#/board", label: "Lifecycle" },
  { hash: "#/tasks", label: "Tasks" },
  { hash: "#/graph", label: "Dependencies" },
  { hash: "#/settings", label: "Settings" },
];

/* Which tab a route belongs under. A detail page highlights the list it came
   from -- looking at Harness Engineering, you are still in Services -- which
   is what stops the navigation going blank the moment you click into
   something. */
function tabFor(route: Route): string {
  switch (route.view) {
    case "portfolio":
    case "object":
    case "new":
      return route.kind === "service" ? "#/" : "#/assets";
    case "strategy":
    case "bet":
    case "initiative":
      return "#/strategy";
    case "board":
      return "#/board";
    case "tasks":
    case "task":
      return "#/tasks";
    case "graph":
      return "#/graph";
    case "settings":
      return "#/settings";
  }
}

export default function App() {
  const route = useRoute();
  const { meta, loading, error } = useStore();
  const user = useUser();
  const signOut = useSignOut();
  const current = tabFor(route);

  return (
    <div className="app">
      <header className="topbar">
        <div className="brand">
          <span>SPD</span> Services
        </div>
        <nav className="tabs">
          {TABS.map((tab) => (
            <button
              key={tab.hash}
              onClick={() => (window.location.hash = tab.hash)}
              aria-current={current === tab.hash ? "page" : undefined}
            >
              {tab.label}
            </button>
          ))}
        </nav>
        <div className="spacer" />
        <div className="sync">
          {user && !user.auth_enabled && (
            /* Said plainly and permanently. An app with sign-in switched off
               should never let anyone believe otherwise. */
            <>
              demo mode · no sign-in
              <br />
            </>
          )}
          <span className="muted">
            {user?.name ?? meta?.me.email}
            {user && user.auth_enabled && ` · ${user.role}`}
          </span>
          {user?.auth_enabled && (
            <>
              {" · "}
              <button className="linkbtn" onClick={signOut}>
                sign out
              </button>
            </>
          )}
        </div>
      </header>

      <main className="wide">
        {error && <div className="err">{error}</div>}
        {loading && <p className="note">Loading the portfolio…</p>}
        {!loading && (
          <>
            {/* Keyed on kind so switching between Services and Assets starts
                with each page's own filters rather than carrying one set of
                class selections onto a table that has none of those classes. */}
            {route.view === "portfolio" && <Portfolio key={route.kind} kind={route.kind} />}
            {route.view === "new" && <ObjectNew key={route.kind} kind={route.kind} />}
            {route.view === "strategy" && <Strategy />}
            {route.view === "bet" && <BetDetail key={route.id} id={route.id} />}
            {route.view === "initiative" && (
              <InitiativeDetail key={route.id} id={route.id} />
            )}
            {route.view === "board" && <Board />}
            {route.view === "tasks" && <Tasks />}
            {route.view === "graph" && <Graph />}
            {route.view === "settings" && <Settings />}
            {route.view === "object" && (
              <ObjectDetail key={`${route.kind}-${route.id}`} kind={route.kind} id={route.id} />
            )}
            {route.view === "task" && <TaskDetail key={route.id} id={route.id} />}
          </>
        )}
      </main>
    </div>
  );
}
